import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PerfHubSectionInfo from './PerfHubSectionInfo';
import { PERF_HUB_OVERVIEW_HELP } from './perfHubOverviewHelp';

const CONFIDENCE_LABELS = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence',
};

const COPY_FEEDBACK_MS = 1800;

/**
 * Robust clipboard copy. Returns a Promise<boolean> — true on success,
 * false on failure. Falls back to ``document.execCommand('copy')`` when
 * ``navigator.clipboard`` isn't available (HTTP origins / older Safari).
 */
async function copyTextRobust(text) {
  if (!text) return false;

  if (
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === 'function' &&
    typeof window !== 'undefined' &&
    window.isSecureContext !== false
  ) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the textarea fallback below.
    }
  }

  if (typeof document === 'undefined') return false;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '0';
  textarea.style.opacity = '0';
  textarea.style.pointerEvents = 'none';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  document.body.removeChild(textarea);
  return ok;
}

/** Preserve order; drop empty or duplicate paragraphs (trimmed). */
function dedupeCopyLines(lines) {
  const seen = new Set();
  const out = [];
  for (const line of lines) {
    const key = String(line || '').trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

/**
 * Build the full multi-paragraph learnings text that goes to the clipboard.
 * Mirrors what the panel renders so users can paste a complete brief into
 * a doc / chat instead of getting just a one-line headline.
 */
function buildLearningsText({ summary, correlations, analyzedCount }) {
  const lines = [];
  lines.push('Creative DNA — learnings');

  const metaBits = [];
  const conf = CONFIDENCE_LABELS[summary.confidence] || null;
  if (conf) metaBits.push(conf);
  if (analyzedCount) {
    metaBits.push(`${analyzedCount} synced post${analyzedCount === 1 ? '' : 's'}`);
  }
  if (metaBits.length) lines.push(metaBits.join(' · '));

  if (summary.headline) lines.push(summary.headline);

  const blocks = summary.copy_blocks || [];
  for (const block of blocks) {
    if (block) lines.push(String(block));
  }

  const styles = summary.styles || [];
  for (const st of styles) {
    const styleLines = [];
    if (st.driver_label) styleLines.push(`▸ ${st.driver_label}`);
    if (st.post_count) {
      styleLines.push(
        `${st.post_count} top post${st.post_count === 1 ? '' : 's'} in this segment.`,
      );
    }
    const whatWorked = (st.what_worked || []).filter(Boolean);
    if (whatWorked.length) {
      styleLines.push('What worked:');
      for (const w of whatWorked) styleLines.push(`  • ${w}`);
    }
    const examples = (st.creator_examples || []).filter(Boolean);
    if (examples.length) {
      styleLines.push('Creator examples:');
      for (const ex of examples) {
        const handle = ex.handle || '';
        const fmt = ex.format ? ` · ${ex.format}` : '';
        const quote = ex.quote ? ` — "${ex.quote}"` : '';
        styleLines.push(`  • ${handle}${fmt}${quote}`);
      }
    }
    if (st.brief_tip) styleLines.push(`Brief tip: ${st.brief_tip}`);
    if (st.handles?.length) {
      styleLines.push(`Creators: ${st.handles.join(' · ')}`);
    } else if (!whatWorked.length && !examples.length && st.summary) {
      styleLines.push(String(st.summary));
    }
    if (styleLines.length) lines.push(styleLines.join('\n'));
  }

  if (correlations.length) {
    const corrLines = ['Patterns in this campaign:'];
    for (const c of correlations) {
      const sign = c.direction === 'higher' ? '+' : '−';
      const delta = Math.abs(Number(c.delta_success) || 0);
      const counts = `(${c.present_count || 0} with · ${c.absent_count || 0} without)`;
      const brief = c.brief_line ? ` — ${c.brief_line}` : '';
      corrLines.push(`  • ${c.label || c.id}: ${sign}${delta} pts blended score ${counts}${brief}`);
    }
    lines.push(corrLines.join('\n'));
  }

  if (summary.sort_hint) lines.push(summary.sort_hint);

  return dedupeCopyLines(lines).join('\n\n');
}

export default function PerfHubCreativeDnaPanel({ creativeDna }) {
  const summary = creativeDna?.summary || {};
  const correlations = creativeDna?.correlations || [];
  // ``analyzed_posts`` is the new field; ``connected_posts`` kept as legacy fallback.
  const connected = summary.analyzed_posts ?? summary.connected_posts ?? 0;

  const [copyState, setCopyState] = useState('idle'); // 'idle' | 'copied' | 'error'
  const copyTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
        copyTimerRef.current = null;
      }
    };
  }, []);

  const learningsText = useMemo(
    () => buildLearningsText({ summary, correlations, analyzedCount: connected }),
    [summary, correlations, connected],
  );

  const canCopy = learningsText.trim().length > 0;

  const handleCopyLearnings = useCallback(async () => {
    if (!canCopy) return;
    const ok = await copyTextRobust(learningsText);
    setCopyState(ok ? 'copied' : 'error');
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => {
      setCopyState('idle');
      copyTimerRef.current = null;
    }, COPY_FEEDBACK_MS);
  }, [canCopy, learningsText]);

  const copyButtonLabel =
    copyState === 'copied'
      ? 'Copied!'
      : copyState === 'error'
        ? 'Copy failed — try again'
        : 'Copy learnings';

  if (!creativeDna) return null;

  return (
    <section className="ph-dna-panel" aria-labelledby="ph-dna-heading">
      <div className="ph-dna-head">
        <div>
          <h4 id="ph-dna-heading" className="ph-dna-title">
            <span className="ph-dna-title-icon" aria-hidden>
              🧬
            </span>
            Creative DNA
            <PerfHubSectionInfo label="Creative DNA">
              {PERF_HUB_OVERVIEW_HELP.creativeDna}
            </PerfHubSectionInfo>
          </h4>
          <p className="ph-dna-tagline">
            See what content worked—not only who. Patterns from synced posts in this campaign.
          </p>
        </div>
        <div className="ph-dna-head-actions">
          {canCopy && (
            <button
              type="button"
              className={`btn-outline ph-dna-copy-btn ph-dna-copy-btn--${copyState}`}
              onClick={handleCopyLearnings}
              disabled={copyState === 'copied'}
              aria-live="polite"
              aria-label={
                copyState === 'copied'
                  ? 'Learnings copied to clipboard'
                  : copyState === 'error'
                    ? 'Copy failed, try again'
                    : 'Copy Creative DNA learnings to clipboard'
              }
            >
              {copyState === 'copied' && (
                <span className="ph-dna-copy-btn-icon" aria-hidden>
                  ✓
                </span>
              )}
              <span className="ph-dna-copy-btn-label">{copyButtonLabel}</span>
            </button>
          )}
        </div>
      </div>

      {connected === 0 ? (
        <div className="ph-dna-empty">
          <p>
            {summary.headline ||
              'Add creators with public Instagram links and refresh metrics to see creative patterns for this campaign.'}
          </p>
        </div>
      ) : (
        <>
          <div className="ph-dna-meta">
            <span className={`ph-dna-confidence ph-dna-confidence--${summary.confidence || 'low'}`}>
              {CONFIDENCE_LABELS[summary.confidence] || 'Low confidence'}
            </span>
            <span className="ph-dna-meta-count">
              {connected} synced post{connected === 1 ? '' : 's'}
            </span>
          </div>

          {summary.headline && <p className="ph-dna-headline">{summary.headline}</p>}

          {(summary.styles || []).length > 0 && (
            <ul className="ph-dna-styles">
              {summary.styles.map((st) => (
                <li key={st.driver} className="ph-dna-style-card">
                  <span className="ph-dna-style-label">{st.driver_label}</span>
                  {(st.what_worked?.length > 0 || st.creator_examples?.length > 0) ? (
                    <>
                      <p className="ph-dna-style-intro">
                        {st.post_count} top post{st.post_count === 1 ? '' : 's'} in this segment.
                      </p>
                      <p className="ph-dna-style-section-title">What worked</p>
                      <ul className="ph-dna-style-detail-list">
                        {(st.what_worked || []).map((line) => (
                          <li key={line.slice(0, 48)}>{line}</li>
                        ))}
                      </ul>
                      {st.creator_examples?.length > 0 && (
                        <>
                          <p className="ph-dna-style-section-title">Creator examples</p>
                          <ul className="ph-dna-style-example-list">
                            {st.creator_examples.map((ex) => (
                              <li key={`${ex.handle}-${ex.quote?.slice(0, 24)}`}>
                                <strong>{ex.handle}</strong>
                                {ex.format ? (
                                  <span className="ph-dna-style-example-format"> · {ex.format}</span>
                                ) : null}
                                <blockquote className="ph-dna-style-quote">
                                  &ldquo;{ex.quote}&rdquo;
                                </blockquote>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                      {st.brief_tip && <p className="ph-dna-style-brief-tip">{st.brief_tip}</p>}
                    </>
                  ) : (
                    <p className="ph-dna-style-summary-fallback">{st.summary}</p>
                  )}
                  {st.handles?.length > 0 && (
                    <p className="ph-dna-style-handles">Creators: {st.handles.join(' · ')}</p>
                  )}
                </li>
              ))}
            </ul>
          )}

          {summary.sort_hint && <p className="ph-dna-sort-hint">{summary.sort_hint}</p>}

          {correlations.length > 0 && (
            <div className="ph-dna-correlations">
              <h5 className="ph-dna-subtitle">Patterns in this campaign</h5>
              <ul className="ph-dna-correlation-list">
                {correlations.map((c) => (
                  <li key={c.id} className="ph-dna-correlation-card">
                    <span className="ph-dna-correlation-label">{c.label}</span>
                    <span
                      className={`ph-dna-correlation-delta ph-dna-correlation-delta--${c.direction}`}
                    >
                      {c.direction === 'higher' ? '+' : '−'}
                      {Math.abs(c.delta_success)} pts blended score
                    </span>
                    <p className="ph-dna-correlation-brief">{c.brief_line}</p>
                    <span className="ph-dna-correlation-n">
                      {c.present_count} with · {c.absent_count} without
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}
